import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { restaurantAPI, menuAPI, seedAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Building2, Clock, Plug, UtensilsCrossed, Phone,
  ChevronRight, ChevronLeft, Check, AlertCircle, Loader2
} from "lucide-react";

const STEPS = [
  { icon: Building2, label: "Business Info", key: "business" },
  { icon: Clock, label: "Hours", key: "hours" },
  { icon: Plug, label: "POS Connection", key: "pos" },
  { icon: UtensilsCrossed, label: "Menu Review", key: "menu" },
  { icon: Phone, label: "Go Live", key: "phone" },
];

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix"];

export default function SetupWizard() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  // Step 1 - Business
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [transferPhone, setTransferPhone] = useState("");

  // Step 2 - Hours
  const [hours, setHours] = useState(
    Object.fromEntries(DAYS.map(d => [d, { open: d === "saturday" || d === "sunday" ? "10:00" : "09:00", close: d === "friday" || d === "saturday" ? "22:00" : "21:00", closed: false }]))
  );

  // Step 3 - POS
  const [posType, setPosType] = useState("toast");
  const [posKey, setPosKey] = useState("");
  const [posLocation, setPosLocation] = useState("");
  const [posConnected, setPosConnected] = useState(false);

  // Step 4 - Menu
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSynced, setMenuSynced] = useState(false);

  // Step 5 - Phone
  const [phoneNumber, setPhoneNumber] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [areaCode, setAreaCode] = useState("");

  const handleSetupBusiness = async () => {
    if (!businessName || !address) {
      toast.error("Please fill in business name and address");
      return;
    }
    setLoading(true);
    try {
      await restaurantAPI.setup({ business_name: businessName, address, timezone, transfer_phone: transferPhone });
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHours = async () => {
    setLoading(true);
    try {
      await restaurantAPI.updateHours(hours);
      setStep(2);
    } catch (err) {
      toast.error("Failed to save hours");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPOS = async () => {
    if (!posKey || !posLocation) {
      toast.error("Please enter POS API Key and Location ID");
      return;
    }
    setLoading(true);
    try {
      await restaurantAPI.connectPOS({ pos_type: posType, api_key: posKey, location_id: posLocation });
      setPosConnected(true);
      toast.success(`${posType === "toast" ? "Toast" : "Clover"} POS connected successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect POS");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncMenu = async () => {
    setLoading(true);
    try {
      await menuAPI.syncPOS();
      const [catRes, itemRes] = await Promise.all([menuAPI.getCategories(), menuAPI.getItems()]);
      setMenuCategories(catRes.data);
      setMenuItems(itemRes.data);
      setMenuSynced(true);
      toast.success("Menu synced from POS!");
    } catch (err) {
      toast.error("Failed to sync menu");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchNumbers = async () => {
    setSearchingNumbers(true);
    try {
      const res = await restaurantAPI.searchNumbers(areaCode);
      setAvailableNumbers(res.data.numbers || []);
      if (res.data.numbers?.length === 0) toast.info("No numbers found. Try a different area code.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to search numbers");
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handleProvisionPhone = async (selectedNumber = "") => {
    setLoading(true);
    try {
      const res = await restaurantAPI.provisionPhone(selectedNumber || undefined);
      setPhoneNumber(res.data.phone_number);
      toast.success(`Phone number ${res.data.phone_number} provisioned!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to provision phone number");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    try {
      await restaurantAPI.activate();
      await seedAPI.seedDemo();
      await refreshUser();
      toast.success("AI Agent activated! Welcome to your dashboard.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Activation failed");
    } finally {
      setLoading(false);
    }
  };

  const updateDayHours = (day, field, value) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="setup-wizard">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/"><img src="/zest-logo.png" alt="Zest" className="h-8 w-auto" /></Link>
            <span className="text-sm text-muted-foreground font-medium">Setup</span>
          </div>
          <span className="text-sm text-muted-foreground">Step {step + 1} of {STEPS.length}</span>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="max-w-4xl mx-auto w-full px-6 pt-8">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.key} className="flex items-center gap-2 flex-1">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-white" : isDone ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                }`}>
                  {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 ${isDone ? "bg-green-300" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-8">
        {/* Step 0: Business Info */}
        {step === 0 && (
          <Card className="fade-in" data-testid="setup-step-business">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Business Information</CardTitle>
              <CardDescription>Tell us about your restaurant</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 max-w-lg">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input placeholder="Tony's Pizza" value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="setup-business-name" className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input placeholder="123 Main St, New York, NY 10001" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="setup-address" className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger data-testid="setup-timezone" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace("America/", "").replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Transfer Phone Number (fallback)</Label>
                <Input placeholder="+12125551234" value={transferPhone} onChange={(e) => setTransferPhone(e.target.value)} data-testid="setup-transfer-phone" className="h-11" />
              </div>
              <Button onClick={handleSetupBusiness} disabled={loading} className="h-11" data-testid="setup-next-step-1">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Hours */}
        {step === 1 && (
          <Card className="fade-in" data-testid="setup-step-hours">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Hours of Operation</CardTitle>
              <CardDescription>Set when your restaurant is open</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {DAYS.map(day => (
                <div key={day} className="flex items-center gap-4 py-2">
                  <span className="w-28 text-sm font-medium capitalize">{day}</span>
                  <div className="flex items-center gap-2">
                    <Switch checked={!hours[day].closed} onCheckedChange={(v) => updateDayHours(day, "closed", !v)} data-testid={`setup-hours-${day}-toggle`} />
                    <span className="text-xs text-muted-foreground w-12">{hours[day].closed ? "Closed" : "Open"}</span>
                  </div>
                  {!hours[day].closed && (
                    <>
                      <Input type="time" value={hours[day].open} onChange={(e) => updateDayHours(day, "open", e.target.value)} className="w-32 h-9" data-testid={`setup-hours-${day}-open`} />
                      <span className="text-muted-foreground text-sm">to</span>
                      <Input type="time" value={hours[day].close} onChange={(e) => updateDayHours(day, "close", e.target.value)} className="w-32 h-9" data-testid={`setup-hours-${day}-close`} />
                    </>
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setStep(0)} data-testid="setup-back-1"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={handleSaveHours} disabled={loading} data-testid="setup-next-step-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: POS Connection */}
        {step === 2 && (
          <Card className="fade-in" data-testid="setup-step-pos">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Connect Your POS</CardTitle>
              <CardDescription>Link your POS system to sync orders automatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 max-w-lg">
              {posConnected ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200" data-testid="pos-connected-indicator">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-medium">{posType === "toast" ? "Toast" : "Clover"} POS Connected Successfully</span>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>POS System *</Label>
                    <Select value={posType} onValueChange={setPosType}>
                      <SelectTrigger data-testid="setup-pos-type" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="toast">
                          <span className="flex items-center gap-2">Toast POS</span>
                        </SelectItem>
                        <SelectItem value="clover">
                          <span className="flex items-center gap-2">Clover POS</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{posType === "toast" ? "Toast API Key" : "Clover API Token"}</Label>
                    <Input placeholder={posType === "toast" ? "toast_api_key_xxxxx" : "clover_access_token_xxxxx"} value={posKey} onChange={(e) => setPosKey(e.target.value)} data-testid="setup-pos-key" className="h-11" />
                  </div>
                  <div className="space-y-2">
                    <Label>{posType === "toast" ? "Restaurant External ID" : "Merchant ID"}</Label>
                    <Input placeholder={posType === "toast" ? "xxxxxxxx-xxxx-xxxx-xxxx" : "XXXXXXXXXXXX"} value={posLocation} onChange={(e) => setPosLocation(e.target.value)} data-testid="setup-pos-location" className="h-11" />
                  </div>
                  <Button onClick={handleConnectPOS} disabled={loading} data-testid="setup-connect-pos">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plug className="w-4 h-4 mr-2" />}
                    Validate {posType === "toast" ? "Toast" : "Clover"} Connection
                  </Button>
                </>
              )}
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setStep(1)} data-testid="setup-back-2"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={() => setStep(3)} disabled={!posConnected} data-testid="setup-next-step-3">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Menu Review */}
        {step === 3 && (
          <Card className="fade-in" data-testid="setup-step-menu">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Menu Review</CardTitle>
              <CardDescription>Sync and review your menu from POS</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!menuSynced ? (
                <div className="text-center py-8">
                  <UtensilsCrossed className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Sync your menu from Toast POS</p>
                  <Button onClick={handleSyncMenu} disabled={loading} data-testid="setup-sync-menu">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sync Menu from POS
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {menuCategories.map(cat => (
                    <div key={cat.id} className="border rounded-xl p-4">
                      <h4 className="font-semibold text-base mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{cat.name}</h4>
                      <div className="space-y-2">
                        {menuItems.filter(item => item.category_id === cat.id).map(item => (
                          <div key={item.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50">
                            <div>
                              <span className="text-sm font-medium">{item.name}</span>
                              <span className="text-sm text-muted-foreground ml-2">${item.price.toFixed(2)}</span>
                            </div>
                            <Badge variant={item.available ? "default" : "secondary"} className={item.available ? "bg-green-100 text-green-700 hover:bg-green-100" : ""}>
                              {item.available ? "Available" : "Unavailable"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setStep(2)} data-testid="setup-back-3"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={() => setStep(4)} disabled={!menuSynced} data-testid="setup-next-step-4">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Go Live */}
        {step === 4 && (
          <Card className="fade-in" data-testid="setup-step-phone">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Go Live</CardTitle>
              <CardDescription>Get a Twilio phone number for your AI agent</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 max-w-lg">
              {!phoneNumber ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Search for an available phone number or let us pick one automatically.</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Area code (e.g. 212)"
                      value={areaCode}
                      onChange={(e) => setAreaCode(e.target.value)}
                      className="w-40 h-10"
                      data-testid="setup-area-code"
                    />
                    <Button variant="outline" onClick={handleSearchNumbers} disabled={searchingNumbers} data-testid="setup-search-numbers">
                      {searchingNumbers ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Search Numbers
                    </Button>
                  </div>
                  {availableNumbers.length > 0 && (
                    <div className="space-y-2 border rounded-xl p-3">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Available Numbers</p>
                      {availableNumbers.map((n, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 border border-slate-100">
                          <div>
                            <p className="text-sm font-mono font-medium">{n.phone_number}</p>
                            <p className="text-xs text-slate-400">{n.locality}{n.region ? `, ${n.region}` : ""}</p>
                          </div>
                          <Button size="sm" className="h-8 text-xs" onClick={() => handleProvisionPhone(n.phone_number)} disabled={loading} data-testid={`select-number-${i}`}>
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Select"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-center text-xs text-slate-400">or</div>
                  <Button variant="outline" onClick={() => handleProvisionPhone()} disabled={loading} className="w-full" data-testid="setup-provision-phone">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                    Auto-pick a Number
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <p className="text-sm text-muted-foreground mb-1">Your AI Phone Number</p>
                    <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="provisioned-phone-number">{phoneNumber}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <p className="text-sm font-medium text-green-800 mb-2">Ready to go live!</p>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li className="flex items-center gap-2"><Check className="w-3 h-3" /> Business info configured</li>
                      <li className="flex items-center gap-2"><Check className="w-3 h-3" /> Hours set</li>
                      <li className="flex items-center gap-2"><Check className="w-3 h-3" /> POS connected</li>
                      <li className="flex items-center gap-2"><Check className="w-3 h-3" /> Menu synced</li>
                      <li className="flex items-center gap-2"><Check className="w-3 h-3" /> Phone number provisioned</li>
                    </ul>
                  </div>
                  <Button onClick={handleActivate} disabled={loading} className="w-full h-12 text-base font-semibold" data-testid="setup-activate">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Activate AI Agent
                  </Button>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="secondary" onClick={() => setStep(3)} data-testid="setup-back-4"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
